import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, contactInfo as defaultContactInfo } from "../i18n/translations";
import { deepMerge } from "../lib/deepMerge";
import { useSiteContent } from "./SiteContentContext";

const LanguageContext = createContext(null);

const STORAGE_KEY = "sd_lang";

export function LanguageProvider({ children }) {
  const { payload } = useSiteContent();

  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "ro";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "ro" || saved === "en" ? saved : "ro";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useMemo(
    () => deepMerge(translations[lang], payload[lang] || {}),
    [lang, payload]
  );

  const contactInfo = useMemo(
    () => deepMerge({ ...defaultContactInfo }, payload.contactInfo || {}),
    [payload.contactInfo]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang: () => setLang((prev) => (prev === "ro" ? "en" : "ro")),
      t,
      contactInfo,
    }),
    [lang, t, contactInfo]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
